import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { getServerErrorMessage } from "utils/showMutationError";

/**
 * End a pregnancy, with the reason it ended (KIN-1942, backend KIN-1940).
 *
 * Its own endpoint rather than the `/pregnancy` upsert `useUpdatePregnancy` posts to: this is a
 * state change with its own rules, and routing it through the upsert would mean sending the whole
 * record back to set one flag.
 *
 * A 409 is not treated as a failure. It means the pregnancy is already completed — someone else
 * got there first, or the button was pressed twice — and the honest response is to show the state
 * that exists rather than an error about a thing that has already happened. Everything else
 * surfaces the backend's own words, which say which of "no reason given" or "not found" it was.
 */
export const useCompletePregnancy = ({ tenantVal, pregnancyId, onSuccess }) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (reason) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/pregnancy/${pregnancyId}/complete`,
        { reason }
      );

      return response.data;
    },
    onSuccess: () => {
      toast.success("Pregnancy completed.");
      queryClient.invalidateQueries({ queryKey: ["pregnancies"] });
      onSuccess?.();
    },
    onError: (error) => {
      if (error?.response?.status === 409) {
        toast("This pregnancy was already completed.");
        queryClient.invalidateQueries({ queryKey: ["pregnancies"] });
        onSuccess?.();

        return;
      }

      console.error(error);
      toast.error(
        getServerErrorMessage(error, "The pregnancy could not be completed.")
      );
    },
  });
};
